import { TableColumn } from "../components/Table";
import Leave from "../models/Leave";
import Loan from "../models/Loan";
import Permission from "../models/Permission";
import type User from "../models/User";
import { sendRequest } from "./api";
import { initAllStores } from "./cache";
import { formatDate } from "./date";
import { capitalizeFirst } from "./string";

export const userConflict = async (userName: string, email: string, userId: string) => {
  const cache = await initAllStores();

  const userByName: User | null = await cache.getBy("staffs", { userName }, { id: userId });
  const userByEmail: User | null = await cache.getBy("staffs", { email }, { id: userId });

  if (userByName && userByEmail) {
    return {
      status: "conflict",
      field: "userName & email",
      message: "A staff member with this username and email already exists in your hotel."
    };
  }

  if (userByName) {
    return {
      status: "conflict",
      field: "userName",
      message: "A staff member with this username already exists in your hotel."
    };
  }

  if (userByEmail) {
    return {
      status: "conflict",
      field: "email",
      message: "A staff member with this email already exists in your hotel."
    };
  }

  return {
    status: "ok",
    message: "No conflicts found. You can proceed with creating this staff."
  };
};



export async function filterStaffsByRole(
  staffs: User[], isCurrentStaff: boolean = true
) {
  const cache = await initAllStores();
  try {
    const currentUser: User | null = await cache.getBy("data", { id: "user" });
    if (!currentUser) throw new Error("User not found.");
    const currentUserRole = currentUser.role;

    return staffs.filter((staff) => {
      // 🚫 Always hide superAdmin from the list
      // if (staff.role === "superAdmin") {
      //   return false;
      // }

      // if (currentUserRole === "superAdmin") {
      //   return !staff.isDelete === isCurrentStaff; // show all except superAdmin
      // }

      // if (currentUserRole === "admin") {
      //   return !staff.isDelete === isCurrentStaff; // hide admins
      // }

      // default: only staff
      return !staff.isDelete === isCurrentStaff;
    });
  } catch (err: any) {
    throw err;
  }
}


export const synchroniseStaffs = async (): Promise<User[]> => {
  const cache = await initAllStores();
  let staffs: User[] = [];
  try {
    const staffs: User[] = await sendRequest("GET", "/users");

    // Clear and update services.
    await cache.clear("staffs");
    await cache.saveMany({ staffs });
    const filteredStaffs = filterStaffsByRole(staffs);
    return filteredStaffs;
  } catch (err: any) {
    console.log(err);
  }
  return staffs;
};


export const getUser = async (): Promise<{ user: User } | false> => {
  try {
    const cache = await initAllStores();
    const user = await cache.getBy("data", { id: "user" });

    if (!user) return false;
    const mappedUser: User = {
      id: user.userId,
      photoUrl: user.photoUrl,
      staffId: user.staffId,
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      userName: user.userName,
      gender: user.gender,
      address: user.address,
      phone: user.phone,
      email: user.email,
      role: user.role,
      state: user.state,
      salary: user.salary,
      payroll: user.payroll,
      startDate: user.startDate,
      dob: user.dob,
      nationality: user.nationality,
      leaveQuota: user.leaveQuota,
      leaveUsedDays: user.leaveUsedDays,
      nokName: user.nokName,
      nokPhone: user.nokPhone,
      nokEmail: user.nokEmail,
      nokAddress: user.nokAddress,
      nokRelation: user.nokRelation,
      portfolio: user.portfolio,
      permissions: user.permissions,
      settings: user.settings,
    };

    return { user: mappedUser, };

  } catch (err: any) {
    console.log(err)

    return false;
  }
}

export const requestTableColumn = (
  type: RequestType, showName: boolean
): any[] => {
  // Status column shared by both types
  // const statusColumn: TableColumn = {
  //   key: "status",
  //   label: "Status",
  //   getClassName: (value: string) => {
  //     const status = value.toLowerCase();
  //     return status === "pending"
  //       ? "text-yellow-600 font-semibold"
  //       : status === "approved"
  //         ? "text-green-600 font-semibold"
  //         : "text-red-600 font-semibold";
  //   },
  // };

  // Common columns for both types
  const commonColumns: TableColumn[] = [
    { key: "createdAt", label: "Application Date", responsive: "md" },
    // { key: "decisionDate", label: "Decision Date", responsive: "md" },
  ];

  // Type-specific columns
  const typeColumns: TableColumn[] =
    type === "loan"
      ? [
        { key: "amount", label: "Amount", },
        { key: "month", label: "Term", },

      ]
      : [
        { key: "start", label: "Start Date", responsive: "md", },
        { key: "end", label: "End Date", responsive: "md", },
      ];

  // Merge type-specific and common columns
  return [
    ...(showName ? [
      //  { key: "staffId", label: "Staff Id", responsive: "md"  },
      {
        key: "staffName",
        label: "Staff Name",
        getClassName: () => "capitalize"
      },
    ] : []),
    ...typeColumns,
    ...commonColumns,
    //  ...[statusColumn,]
  ];
};


export async function fetchAndSetRequests<T extends Loan | Leave>(
  endpoint: string,
  setter: React.Dispatch<React.SetStateAction<T[]>>,
  type: "loan" | "leave"
) {
  try {
    const res = await sendRequest("GET", endpoint);

    const mappedData = (res as T[]).map((item: any) => ({
      id: item.id,
      createdAt: formatDate(item.createdAt),
      decisionDate: item.decisionDate
        ? formatDate(item.decisionDate)
        : "Pending Review",
      staffName: `${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`.trim(),
      staffId: item?.user?.staffId,
      staffRole: item?.user?.role,
      staffPortfolio: item?.user?.portfolio,
      ...(type === "loan"
        ? {
          amount: `${item.amount.toLocaleString()}`,
          month: item.month,
          reason: item.reason,
          status: capitalizeFirst(item.status),
          approvals: item.loanApprovals
        }
        : {
          start: formatDate(item.start),
          end: formatDate(item.end),
          reason: item.reason,
          status: capitalizeFirst(item.status),
          approvals: item.leaveApprovals
        }),
    }));

    setter(mappedData as []);
  } catch (error: any) {
    console.error("Failed to fetch requests:", error);
    // showToast(`Failed to fetch ${capitalizeFirst(type)} requests`, { type: "error" });
  }
}


export function initPayroll(staff: User, type: "earn" | "deduct", payrolls?: Record<string, number>) {
  const earnings: PayRoll[] = [];

  if (!payrolls) {
    if (type === "earn") {
      return [
        {
          title: "Basic Salary",
          amount: staff.salary?.toString() ?? "0",
        },
        {
          title: "Transportation",
          amount: "0",
        },
        {
          title: "Housing",
          amount: "0"
        }
      ];
    } else return [];
  };
  const allpayRolls = Object.entries(payrolls);

  for (const pl of allpayRolls) {
    const payrollItem = { title: pl[0], amount: String(pl[1]) };
    if (pl[0].toLocaleLowerCase() === "basic salary") {
      earnings.unshift(payrollItem)
    }
    else earnings.push(payrollItem);
  }

  return earnings;
}


/* Generate payroll table columns based on the page context */
export const payRollColumns = (pages: "profile" | "staff") => {
  const columns: TableColumn[] = [];

  // Conditionally include Employee Name column
  if (pages === "staff") {
    columns.push({
      key: "name",
      label: "Employee Name",
      getClassName: () => "capitalize",
    },
      {
        key: "createdAt",
        label: "Payment Date",
        responsive: "lg",
      },
      {
        key: "position",
        label: "Position",
        responsive: "lg",
        getClassName: () => "capitalize",
      },
      {
        key: "accountNo",
        label: "Account No.",
        responsive: "lg",
      });
  }

  columns.push(

    {
      key: "totalEarnings",
      label: "Total Earnings",
      responsive: pages === "staff" ? "md" : "all",
      getClassName: () => "font-semibold text-green-700",
    },
    {
      key: "totalDeductions",
      label: "Total Deductions",
      getClassName: () => "font-semibold text-red-600",
      responsive: "md",
    },
    {
      key: "netPay",
      label: "Net Pay",
      getClassName: (value: string) =>
        parseFloat(value.replace(",", "")) > 0
          ? "font-bold text-green-800"
          : "font-bold text-red-500",
    },

  );

  return columns;
};


export const totalErnings = (user: User) => {

  const earnings = Object.entries(user?.payroll?.earnings ?? {});

  const total = earnings.reduce(
    (sum: number, p: any) => sum + p[1], 0
  );

  const basicSalary = user.salary ?? 0;
  return total + (earnings.length ? 0 : basicSalary);
}

export const terminal = () => {
  return localStorage.getItem("terminal") ?? "";
}


export const isCeo = (portfolio: string) => {
  return portfolio?.trim().toLowerCase() === "ceo"
}

export const isMd = (portfolio: string) => {
  return portfolio?.trim().toLowerCase() === "managing director" ||
    portfolio.trim().toLocaleLowerCase() === "md"
}


export const getUserAvatar = (user: {
  firstName: string;
  lastName: string;
  photoUrl?: string | null;
}) => {
  return (
    user.photoUrl ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      `${user.firstName} ${user.lastName}`
    )}&background=e0e7ff&color=4338ca`
  );
};


export const hasAccess = async (
  name: string,
  type: "action" | "view",
  // Permissions: Permission[]
): Promise<boolean> => {

  const response = await getUser();
  if (!response) return false;

  const { user } = response;

  const permType = type.toLocaleLowerCase();
  const perName = name.toLowerCase()
  const perm = (user.permissions ?? []).find(perm =>
    perm.value &&
    perm.type.toLocaleLowerCase() === permType &&
    perm.name.toLocaleLowerCase() == perName
  );

  return !!perm;
}